import { useMemo } from 'react';
import { Locale } from 'libs.i18n-core';
import { AssetTypes, LocalizationStatus, } from 'libs.localization.common';
import { useLocalizationCollection } from 'libs.localization.react';
import { useNotificationsLoader } from './use_notifications_loader.hook';
import { StudyAssetType } from '../types';
import { getNotificationTemplateRole, getVersionStatus } from '../utils/data_utils';
import { toSnakeCase } from '../utils/string_utils';
function getType(type) {
    switch (type) {
        case 'push':
            return AssetTypes.NOTIFICATION_PUSH_NOTIFICATION;
        case 'sms':
            return AssetTypes.NOTIFICATION_SMS;
        case 'email':
            return AssetTypes.NOTIFICATION_EMAIL;
        default:
            return AssetTypes.NOTIFICATION_PUSH_NOTIFICATION;
    }
}
export const useNotificationsData = (studyId, participantLocales = [], siteLocales = [], notifications = []) => {
    const { templates, isLoading: areTemplatesLoading } = useNotificationsLoader(studyId);
    const assets = templates?.map(({ id, data: { type } }) => ({
        type: getType(type),
        id,
        version: '1',
    }));
    const locales = Array.from(new Set([...participantLocales, ...siteLocales]));
    // A template's audience is derived from the study notification that references it
    // (notification.templateResourceId === template.id). Site-facing notifications only
    // need site locales, participant-facing ones only need participant locales; templates
    // no study notification references are participant-facing.
    const notificationByTemplateId = new Map(notifications.map((notification) => [notification.templateResourceId, notification]));
    const { localizationsMap, isLoading } = useLocalizationCollection(assets);
    const isLoadingNotifications = areTemplatesLoading || isLoading;
    const _data = useMemo(() => {
        if (!templates?.length) {
            return {};
        }
        const data = {};
        templates.forEach((template) => {
            const localeData = {};
            for (const locale of locales) {
                localeData[locale] = locale === Locale.en_US ? LocalizationStatus.READY : LocalizationStatus.NOT_STARTED;
            }
            const _asset = assets.find((a) => a.id === template.id);
            const { localizationEntry, localization, } = localizationsMap?.get(_asset) || {};
            const translationWorkbenchId = localization?.getId();
            const referencingNotification = notificationByTemplateId.get(template.id);
            const role = getNotificationTemplateRole(referencingNotification);
            const versionStatus = getVersionStatus({
                localizationEntry,
                participantLocales,
                siteLocales,
                role,
                translationWorkbenchId,
                assetType: getType(template.data.type),
                isLoading: isLoadingNotifications,
            });
            data[translationWorkbenchId] = {
                displayName: template.name,
                translationWorkbenchId,
                id: template.id,
                type: StudyAssetType.NOTIFICATIONS,
                path: template.id,
                statuses: localeData,
                role,
                isUnreferenced: !referencingNotification,
                assetType: getType(template.data.type),
                ...versionStatus,
                asset: {
                    type: getType(template.data.type),
                    id: template.id,
                    version: '1',
                    human_id: toSnakeCase(template.name),
                    resource_id: template.id,
                    messages: [
                        {
                            namespace: template.id,
                            messages: ['plain', 'subject', 'html'].reduce((acc, key) => {
                                if (template.data[key]) {
                                    acc[key] = template.data[key];
                                }
                                return acc;
                            }, {}),
                        },
                    ],
                    preview: {
                        engine: 'react',
                        id: getType(template.data.type),
                        default_viewport: 'iosSmall',
                        info: {
                            display_name: template.name,
                            list: [],
                        },
                    },
                },
            };
        });
        return data;
    }, [templates, localizationsMap, isLoadingNotifications, notifications]);
    return { data: _data, isLoading: isLoadingNotifications };
};
